/**
 * sharedParticleBuffers.ts
 *
 * The particle physics loop runs in a dedicated Web Worker (see
 * workers/particlePhysicsWorker.ts) so it no longer competes with rendering for main-thread
 * time -- see particles.ts's module doc comment for why that matters. Everything the MAIN
 * THREAD'S renderer needs to read every frame (trail history positions/colors, current head
 * slot, and the few per-particle scalars the renderer still computes final colors/sizes from)
 * lives in SharedArrayBuffer-backed typed arrays allocated here, so the worker can write into
 * them and the renderer can read them with zero per-frame copying/message-passing.
 *
 * Everything else (lat/lon, true altitude, age, grounded/persistence timers, fade state, the
 * density grid, ...) stays entirely private to the worker -- the renderer never needs it.
 *
 * Note: reads/writes to these arrays are NOT synchronized with Atomics. A reader may
 * occasionally see a position update mid-write (e.g. x/y updated but not yet z) -- for a
 * continuously-animated visualization redrawn at 60fps, an imperceptible single-frame tear is
 * an acceptable tradeoff for avoiding the overhead/complexity of locking on every sample.
 */

export interface SharedParticleBuffers {
  count: number;
  trailLength: number;
  /** Ring buffer of recent world-space positions per particle: count * trailLength * 3 floats. */
  history: Float32Array;
  /** Parallel ring buffer of each history point's color at record time: count * trailLength * 3 floats. */
  historyColor: Float32Array;
  /** Current head slot index per particle (int32 for safe plain shared reads/writes). */
  headIndex: Int32Array;
  /** Combined fade-in/fade-out opacity multiplier (0..1) per particle. */
  lifecycleAlpha: Float32Array;
  /** Current temperature (K) per particle, used by the renderer to recolor the head marker. */
  temperatureK: Float32Array;
  /** Current horizontal speed (m/s) per particle, used to size the head marker/trail width. */
  horizontalSpeedMs: Float32Array;
}

function sharedFloat32(length: number): Float32Array {
  return new Float32Array(new SharedArrayBuffer(length * Float32Array.BYTES_PER_ELEMENT));
}

function sharedInt32(length: number): Int32Array {
  return new Int32Array(new SharedArrayBuffer(length * Int32Array.BYTES_PER_ELEMENT));
}

export function createSharedParticleBuffers(count: number, trailLength: number): SharedParticleBuffers {
  return {
    count,
    trailLength,
    history: sharedFloat32(count * trailLength * 3),
    historyColor: sharedFloat32(count * trailLength * 3),
    headIndex: sharedInt32(count),
    lifecycleAlpha: sharedFloat32(count),
    temperatureK: sharedFloat32(count),
    horizontalSpeedMs: sharedFloat32(count)
  };
}
